import { createFileRoute } from '@tanstack/react-router'
import {
	ClientSideRowModelModule,
	ColDef,
	ICellRendererParams,
	ModuleRegistry,
	NumberEditorModule,
	TextEditorModule,
	ValidationModule
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import {
	createContext,
	useContext,
	useMemo,
	useState,
	forwardRef,
	useImperativeHandle,
	useRef,
	useCallback
} from 'react'

import { TaxRate } from '@/api/admin-country-types'
import { Price, PriceInput } from '@/api/admin-products-types'
import { SalesChannelListItem } from '@/api/admin-sales-channels-types'
import { useUpdateProductPricing } from '@/api/api-mutations'
import { EndpointResponse } from '@/api/common'
import { Endpoint } from '@/api/common'
import { PricingMethodType, PricingMethod } from '@/api/common-types'
import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { FocusModal } from '@/components/base/focus-modal'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { useRouteModal } from '@/components/route-modal'
import { RouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { ChevronDown } from '@/icons/chevron-down'
import { ChevronRight } from '@/icons/chevron-right'
import { fromPrecision, getCurrencyDecimals, toPrecision } from '@/utils/currency'
import { useAsyncEffect } from '@/utils/hooks'
import { sortArray } from '@/utils/utilities'

export const Route = createFileRoute('/_authenticated/_general/products/$productUuid/prices')({
	component: ProductPrices
})

function ProductPrices() {
	const { productUuid } = Route.useParams()

	return (
		<RouteModal requiresData loader={async api => await api.getProductPricing({ path: { productUuid } })}>
			{data => <ProductPricesForm data={data} />}
		</RouteModal>
	)
}

function ProductPricesForm({ data }: { data: EndpointResponse<Endpoint.GetProductPricing> }) {
	const { L } = useI18n()
	const { productUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const gridRef = useRef<ProductPricesGridRef>(null)

	const updateProductPricing = useUpdateProductPricing(true)
	const handleSave = useCallback(() => {
		const newPrices = gridRef.current?.getGridData()

		if (!newPrices) {
			return
		}

		updateProductPricing.mutateAsync(
			{
				path: { productUuid },
				body: newPrices
			},
			{
				onSuccess: () => {
					handleSuccess('..')
				},
				onError: e => {
					toast.error(e.message)
				}
			}
		)
	}, [productUuid, handleSuccess, updateProductPricing])

	return (
		<div className='flex size-full flex-col'>
			<FocusModal.Header>
				<FocusModal.Title>
					<Heading>{L.Product.Pricing.Title()}</Heading>
				</FocusModal.Title>
			</FocusModal.Header>
			<FocusModal.Body className='flex flex-col overflow-hidden'>
				<ProductPricesGrid
					ref={gridRef}
					initialPrices={data.prices}
					pricingMethod={data.pricingMethod}
					units={data.unitsOfMeasure}
				/>
			</FocusModal.Body>
			<FocusModal.Footer>
				<div className='flex w-full items-center justify-end gap-x-2'>
					<FocusModal.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</FocusModal.Close>
					<Button
						onClick={handleSave}
						variant='primary'
						size='small'
						isLoading={updateProductPricing.isPending}
					>
						{L.General.Save()}
					</Button>
				</div>
			</FocusModal.Footer>
		</div>
	)
}

// Register AG Grid modules
ModuleRegistry.registerModules([ClientSideRowModelModule, NumberEditorModule, TextEditorModule, ValidationModule])

const disabledCellStyle: ColDef<RowData>['cellStyle'] = params =>
	params.data?.enabled ? null : { color: '#888', backgroundColor: '#f5f5f5' }

// Groups first (each followed by its member channels), then ungrouped channels. Within a group,
// the group row sits above its members. Sort using the active dashboard locale, and fall back to
// the (unique) target uuid when names compare equal.
const orderRows = (rows: RowData[], locale: string): RowData[] =>
	[...rows].sort((a, b) => {
		if ((a.unitOfMeasureUuid || b.unitOfMeasureUuid) && a.isBase !== b.isBase) {
			return a.isBase ? -1 : 1
		}
		if (a.unitOfMeasureUuid && b.unitOfMeasureUuid && a.unitLabel !== b.unitLabel) {
			return (a.unitLabel ?? '').localeCompare(b.unitLabel ?? '', locale)
		}
		const aGroupKey = a.targetType === 'group' ? a.targetUuid : (a.groupUuid ?? '')
		const bGroupKey = b.targetType === 'group' ? b.targetUuid : (b.groupUuid ?? '')
		const aIsGrouped = aGroupKey !== ''
		const bIsGrouped = bGroupKey !== ''
		if (aIsGrouped !== bIsGrouped) {
			return aIsGrouped ? -1 : 1
		}
		if (aIsGrouped) {
			const aGroupName = a.targetType === 'group' ? a.targetName : (a.groupName ?? '')
			const bGroupName = b.targetType === 'group' ? b.targetName : (b.groupName ?? '')
			const byGroupName = aGroupName.localeCompare(bGroupName, locale)
			if (byGroupName !== 0) {
				return byGroupName
			}
			if (aGroupKey !== bGroupKey) {
				return aGroupKey.localeCompare(bGroupKey)
			}
			if (a.targetType !== b.targetType) {
				return a.targetType === 'group' ? -1 : 1
			}
		}
		if (a.enabled !== b.enabled) {
			return a.enabled ? -1 : 1
		}
		const byName = a.targetName.localeCompare(b.targetName, locale)
		if (byName !== 0) {
			return byName
		}
		return a.targetUuid.localeCompare(b.targetUuid)
	})

// A price row targets either a sales channel group (the default price for every member channel) or
// a single sales channel (an override that wins over its group's default).
export type PriceTargetType = 'group' | 'channel'

export type RowData = {
	enabled: boolean
	targetType: PriceTargetType
	targetUuid: string
	targetName: string
	currencyCode: string
	price: number | null
	variablePrice: boolean
	taxRate: string
	taxInclusive: boolean
	unitOfMeasureUuid?: string
	unitLabel?: string
	isBase?: boolean
	// Set on channel rows that belong to a group so they nest under that group when expanded.
	groupUuid?: string
	groupName?: string
}

// Key uniquely identifying a (unit, target) pair within the grid.
const rowKey = (row: Pick<RowData, 'targetType' | 'targetUuid' | 'unitOfMeasureUuid'>) =>
	`${row.unitOfMeasureUuid ?? ''}:${row.targetType}:${row.targetUuid}`

type PricingUnit = {
	uuid: string
	code: string
	name?: string
	isBase: boolean
}

const unitLabel = (unit: PricingUnit) => unit.code

export interface ProductPricesGridRef {
	getGridData: () => PriceInput[] | null
}

type PricingGridContextValue = {
	expandedGroupUuids: Set<string>
	toggleGroupExpanded: (groupUuid: string) => void
	channelsByGroup: Map<string, SalesChannelListItem[]>
	rowData: RowData[]
}

const PricingGridContext = createContext<PricingGridContextValue | null>(null)

function TargetNameCell(params: ICellRendererParams<RowData>) {
	const { L } = useI18n()
	const ctx = useContext(PricingGridContext)
	const row = params.data
	if (!row || !ctx) {
		return null
	}
	if (row.targetType !== 'group') {
		if (row.groupUuid) {
			return <span className='pl-6'>{row.targetName}</span>
		}
		return row.targetName
	}
	const memberCount = (ctx.channelsByGroup.get(row.targetUuid) ?? []).length
	if (memberCount === 0) {
		return row.targetName
	}
	const overrideCount = ctx.rowData.filter(
		candidate =>
			candidate.targetType === 'channel' &&
			candidate.groupUuid === row.targetUuid &&
			candidate.unitOfMeasureUuid === row.unitOfMeasureUuid &&
			candidate.enabled
	).length
	const expanded = ctx.expandedGroupUuids.has(row.targetUuid)
	return (
		<div className='flex h-full items-center gap-1'>
			<button
				type='button'
				className='flex items-center'
				aria-expanded={expanded}
				aria-label={expanded ? L.Product.Pricing.CollapseGroup() : L.Product.Pricing.ExpandGroup()}
				onMouseDown={event => {
					event.preventDefault()
					event.stopPropagation()
					ctx.toggleGroupExpanded(row.targetUuid)
				}}
			>
				{expanded ? <ChevronDown className='h-4 w-4' /> : <ChevronRight className='h-4 w-4' />}
			</button>
			<span>{row.targetName}</span>
			{overrideCount > 0 ? (
				<span className='text-ui-fg-muted'>({L.Product.Pricing.OverrideCount({ count: overrideCount })})</span>
			) : null}
		</div>
	)
}

interface ProductPricesGridProps {
	pricingMethod: PricingMethodType
	initialPrices: Price[]
	units?: PricingUnit[]
}

export const ProductPricesGrid = forwardRef<ProductPricesGridRef, ProductPricesGridProps>(
	({ initialPrices, pricingMethod, units }, ref) => {
		const { L, locale } = useI18n()
		const gridRef = useRef<AgGridReact<any>>(null)
		const [rowData, setRowData] = useState<RowData[]>([])

		const isVariablePrice = useMemo(() => pricingMethod === PricingMethod.PriceEmbedded, [])

		const [taxRates, setTaxRates] = useState<TaxRate[]>([])
		const [salesChannels, setSalesChannels] = useState<SalesChannelListItem[]>([])
		const [loading, setLoading] = useState(false)
		const [expandedGroupUuids, setExpandedGroupUuids] = useState<Set<string>>(() => new Set())
		const { api } = useApiContext()
		const rowDataRef = useRef(rowData)
		rowDataRef.current = rowData

		const rowUnits = useMemo<(PricingUnit | undefined)[]>(
			() => (units && units.length > 0 ? units : [undefined]),
			[units]
		)

		// Builds a grid row for a (target, unit) pair, pre-filling price/tax from an existing price.
		const buildRow = useCallback(
			(args: {
				targetType: PriceTargetType
				targetUuid: string
				targetName: string
				currencyCode: string
				price: Price | undefined
				unit: PricingUnit | undefined
				allTaxRates: TaxRate[]
				groupUuid?: string
				groupName?: string
			}): RowData => {
				const {
					targetType,
					targetUuid,
					targetName,
					currencyCode,
					price,
					unit,
					allTaxRates,
					groupUuid,
					groupName
				} = args
				const taxRate = allTaxRates.find(x => x.uuid === price?.priceInfo?.taxRateUuid)
				let amount: number | null = 0
				if (isVariablePrice) {
					amount = null
				} else if (price && !price.priceInfo.variablePrice) {
					amount = fromPrecision(price.priceInfo.amount)
				}
				return {
					enabled: !!price?.priceInfo,
					targetType,
					targetUuid,
					targetName,
					currencyCode,
					variablePrice: isVariablePrice,
					price: amount,
					taxRate: taxRate?.name ?? allTaxRates[0].name,
					taxInclusive: price?.priceInfo?.taxInclusive ?? true,
					...(unit
						? {
								unitOfMeasureUuid: unit.uuid,
								unitLabel: unitLabel(unit),
								isBase: unit.isBase
							}
						: {}),
					...(groupUuid ? { groupUuid, groupName } : {})
				}
			},
			[isVariablePrice]
		)

		const isInitializing = useRef(false)
		useAsyncEffect(async () => {
			if (isInitializing.current) {
				return
			}
			isInitializing.current = true
			try {
				setLoading(true)
				const [salesChannelsData, taxRatesData, groupsData] = await Promise.all([
					api.listSalesChannels({}),
					api.listTaxRates({}),
					api.listSalesChannelGroups({})
				])
				setTaxRates(sortArray(taxRatesData, x => x.name))
				sortArray(salesChannelsData, x => x.name)
				setSalesChannels(salesChannelsData)
				const sortedGroups = sortArray([...groupsData], x => x.name)

				// Currency for a group default comes from its member channels (they share a currency in
				// practice); fall back to the first channel so a memberless group still has a sensible default.
				const channelsByGroup = new Map<string, SalesChannelListItem[]>()
				for (const channel of salesChannelsData) {
					if (channel.group) {
						const members = channelsByGroup.get(channel.group.uuid) ?? []
						members.push(channel)
						channelsByGroup.set(channel.group.uuid, members)
					}
				}

				const data: RowData[] = []
				for (const unit of rowUnits) {
					// One default row per group.
					for (const group of sortedGroups) {
						const members = channelsByGroup.get(group.uuid) ?? []
						const currencyCode = members[0]?.currencyCode ?? salesChannelsData[0]?.currencyCode ?? ''
						const price = initialPrices.find(
							candidate =>
								candidate.salesChannelGroup?.uuid === group.uuid &&
								(unit ? candidate.unitOfMeasure?.uuid === unit.uuid : true)
						)
						data.push(
							buildRow({
								targetType: 'group',
								targetUuid: group.uuid,
								targetName: group.name,
								currencyCode,
								price,
								unit,
								allTaxRates: taxRatesData
							})
						)
					}
					// Ungrouped channels always show. Grouped channels only appear once their group is
					// expanded; existing overrides are still kept in the model so they survive a save
					// while the group is collapsed.
					for (const salesChannel of salesChannelsData) {
						const price = initialPrices.find(
							candidate =>
								candidate.salesChannel?.uuid === salesChannel.uuid &&
								(unit ? candidate.unitOfMeasure?.uuid === unit.uuid : true)
						)
						if (salesChannel.group && !price) {
							continue
						}
						data.push(
							buildRow({
								targetType: 'channel',
								targetUuid: salesChannel.uuid,
								targetName: salesChannel.name,
								currencyCode: salesChannel.currencyCode,
								price,
								unit,
								allTaxRates: taxRatesData,
								groupUuid: salesChannel.group?.uuid,
								groupName: salesChannel.group?.name
							})
						)
					}
				}
				setRowData(orderRows(data, locale))
				setLoading(false)
			} catch (e) {
				console.error(e)
			}
		}, [])

		const channelsByGroup = useMemo(() => {
			const membersByGroup = new Map<string, SalesChannelListItem[]>()
			for (const channel of salesChannels) {
				if (!channel.group) {
					continue
				}
				const members = membersByGroup.get(channel.group.uuid) ?? []
				members.push(channel)
				membersByGroup.set(channel.group.uuid, members)
			}
			return membersByGroup
		}, [salesChannels])

		const seedGroupMembers = useCallback(
			(groupUuid: string, prev: RowData[]): RowData[] => {
				const members = channelsByGroup.get(groupUuid) ?? []
				const groupName = prev.find(
					row => row.targetType === 'group' && row.targetUuid === groupUuid
				)?.targetName
				const additions: RowData[] = []
				for (const unit of rowUnits) {
					const groupDefault = prev.find(
						row =>
							row.targetType === 'group' &&
							row.targetUuid === groupUuid &&
							row.unitOfMeasureUuid === unit?.uuid
					)
					for (const channel of members) {
						const newRow: RowData = {
							enabled: false,
							targetType: 'channel',
							targetUuid: channel.uuid,
							targetName: channel.name,
							currencyCode: channel.currencyCode,
							variablePrice: isVariablePrice,
							price: isVariablePrice ? null : (groupDefault?.price ?? 0),
							taxRate: groupDefault?.taxRate ?? taxRates[0]?.name ?? '',
							taxInclusive: groupDefault?.taxInclusive ?? true,
							groupUuid,
							groupName,
							...(unit
								? { unitOfMeasureUuid: unit.uuid, unitLabel: unitLabel(unit), isBase: unit.isBase }
								: {})
						}
						if (!prev.some(row => rowKey(row) === rowKey(newRow))) {
							additions.push(newRow)
						}
					}
				}
				if (additions.length === 0) {
					return prev
				}
				return orderRows([...prev, ...additions], locale)
			},
			[channelsByGroup, rowUnits, isVariablePrice, taxRates, locale]
		)

		const toggleGroupExpanded = useCallback(
			(groupUuid: string) => {
				gridRef.current?.api.stopEditing()
				setExpandedGroupUuids(prev => {
					const next = new Set(prev)
					if (next.has(groupUuid)) {
						next.delete(groupUuid)
					} else {
						next.add(groupUuid)
					}
					return next
				})
				setRowData(prev => seedGroupMembers(groupUuid, prev))
			},
			[seedGroupMembers]
		)

		const visibleRowData = useMemo(
			() =>
				rowData.filter(
					row => row.targetType === 'group' || row.groupUuid == null || expandedGroupUuids.has(row.groupUuid)
				),
			[rowData, expandedGroupUuids]
		)

		const colDefs = useMemo<ColDef<RowData>[]>(
			() =>
				[
					{
						field: 'enabled',
						headerName: L.Product.Pricing.Enabled(),
						editable: true,
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						width: 100,
						flex: 0
					},
					{
						field: 'targetName',
						headerName: L.Product.Pricing.Target(),
						editable: false,
						cellRenderer: TargetNameCell,
						suppressKeyboardEvent: () => true
					},
					!isVariablePrice && {
						field: 'price',
						headerName: L.Product.Pricing.Price(),
						editable: true,
						cellEditor: 'agNumberCellEditor',
						cellEditorParams: {
							min: 0,
							precision: 2
						},
						valueFormatter: params =>
							L.Amount.Default({
								amount: params.value ?? 0,
								currencyCode: params.data?.currencyCode ?? ''
							}),
						cellStyle: disabledCellStyle
					},
					{
						field: 'taxRate',
						headerName: L.Fields.TaxRate(),
						cellEditor: 'agSelectCellEditor',
						cellEditorParams: {
							values: taxRates.map(x => x.name)
						},
						cellStyle: disabledCellStyle
					},
					{
						field: 'taxInclusive',
						headerName: L.Fields.TaxInclusive(),
						cellRenderer: 'agCheckboxCellRenderer',
						cellEditor: 'agCheckboxCellEditor',
						cellStyle: disabledCellStyle
					},
					!!units?.length && {
						field: 'unitLabel',
						headerName: L.Product.UnitsOfMeasure.Unit(),
						editable: false
					}
				].filter(Boolean) as ColDef<RowData>[],
			[taxRates, L, units, isVariablePrice]
		)

		const defaultColDef = useMemo<ColDef>(() => {
			return {
				editable: params => params.data.enabled,
				flex: 1,
				sortable: false
			}
		}, [])

		useImperativeHandle(ref, () => ({
			getGridData: () => {
				gridRef.current?.api.stopEditing()
				const allRowData = rowDataRef.current

				const newPrices: PriceInput[] = []
				for (const row of allRowData) {
					const taxRate = taxRates.find(x => x.name === row.taxRate)
					if (!taxRate) {
						toast.error(L.Product.Pricing.InvalidTaxRate({ taxRate: row.taxRate }))
						return null
					}
					if (!row.enabled) {
						continue
					}
					newPrices.push({
						...(row.targetType === 'group'
							? { salesChannelGroupUuid: row.targetUuid }
							: { salesChannelUuid: row.targetUuid }),
						currencyCode: row.currencyCode,
						...(row.unitOfMeasureUuid ? { unitOfMeasureUuid: row.unitOfMeasureUuid } : {}),
						priceInfo: isVariablePrice
							? {
									variablePrice: true,
									taxRateUuid: taxRate.uuid,
									taxInclusive: row.taxInclusive
								}
							: {
									variablePrice: false,
									amount: row.price
										? toPrecision(row.price, getCurrencyDecimals(row.currencyCode))
										: toPrecision(0),
									taxRateUuid: taxRate.uuid,
									taxInclusive: row.taxInclusive
								}
					})
				}
				return newPrices
			}
		}))

		const gridContext = useMemo<PricingGridContextValue>(
			() => ({
				expandedGroupUuids,
				toggleGroupExpanded,
				channelsByGroup,
				rowData
			}),
			[expandedGroupUuids, toggleGroupExpanded, channelsByGroup, rowData]
		)

		return (
			<PricingGridContext.Provider value={gridContext}>
				<div className='flex flex-grow flex-col gap-y-2'>
					<div className='ag-theme-quartz flex-grow'>
						<AgGridReact
							ref={gridRef}
							loading={loading}
							rowData={visibleRowData}
							columnDefs={colDefs}
							defaultColDef={defaultColDef}
							getRowId={params => rowKey(params.data)}
							singleClickEdit
							onCellValueChanged={e => {
								if (e.colDef.field === 'price') {
									const enabled = typeof e.newValue === 'number' && e.newValue > 0
									if (e.data.enabled !== enabled) {
										e.node.setDataValue('enabled', enabled)
										return
									}
								}
								if (e.colDef.field === 'enabled') {
									// Create a new array reference to trigger re-render for cellStyle.
									// Rows are only sorted on initial load, not while editing, so a
									// toggled row keeps its position instead of jumping around.
									setRowData(prevRowData => [...prevRowData])
								}
							}}
						/>
					</div>
				</div>
			</PricingGridContext.Provider>
		)
	}
)

ProductPricesGrid.displayName = 'ProductPricesGrid'
